import { Grid } from '@material-ui/core';
import moment from 'moment-timezone';
import React from 'react';
import { Link } from 'react-router-dom';

// specific to this view
import { deleteIPTrace, getIPTraces, IIPTrace } from '~/src/dash/api/ip-trace';
import { IIPTraceRowData, IListTableColumn, ListTable } from '~/src/dash/components/list-table';
import { ModelMediumButton } from '~/src/dash/components/model-medium-button';
import { ModelTopBar } from '~/src/dash/components/model-topbar';
import { Panel, Panels } from '~/src/dash/publicaUI';
import { IStore } from '~/src/dash/reducers/store';
import {
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import { publicaColors } from '~/src/dash/utils/colors';
import { decorate } from '~/src/dash/utils/decorator';
import { hasRoleSuperUser } from '~/src/dash/utils/roles';
import { routeName } from '~/src/dash/utils/routes';
import { RowButton } from '~/src/dash/views/component-view';
import { LoadingView } from '~/src/dash/views/loading-view';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {}

interface IState {
  // indicate api call being completed
  updating: boolean;
  // indicates when render is allowed
  loading: boolean;

  snackbarMessage: string;
  snackbarError: boolean;
  ipTraces: IIPTrace[];
}

const columns: IListTableColumn[] = [
  { field: 'StartDate', title: 'Start Date', renderType: 'normal' },
  { field: 'TimeFrame', title: 'TimeFrame', renderType: 'normal' },
  { field: 'IP', title: 'IP', renderType: 'normal' },
  { field: 'Email', title: 'Email', renderType: 'normal' },
  { field: 'UUID', title: 'UUID', renderType: 'normal' },
  { field: 'Actions', title: 'Actions', renderType: 'delete', searchable: false },
];

export const IPTraceListView = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    styles: (theme: any) => ({
      paper: {
        ...theme.mixins.gutters(),
        paddingTop: theme.spacing.unit * 2,
        paddingBottom: theme.spacing.unit * 2,
        marginBottom: theme.spacing.unit * 4,
      },
    }),
    router: true,
  },
  class extends React.Component<IProps, IState> {
    displayName: 'IPTraceListView';

    constructor(props: IProps) {
      super(props);
      this.state = {
        ipTraces: [] as IIPTrace[],
        updating: false,
        loading: false,

        snackbarMessage: '',
        snackbarError: false,
      };
    }

    // --------------- TABLE HANDLERS ---------------
    handleSetUpdating = (updating: boolean) => {
      this.setState({
        updating,
      });
    };

    handleDeleteIPTrace = (ipTraceIDStr: string) => {
      const ipTraceID = parseInt(ipTraceIDStr, 10);
      this.setState(
        {
          updating: true,
        },
        () => {
          deleteIPTrace(ipTraceID, this.props.session.publisher.ID)
            .then(() => {
              const ipTraces = this.state.ipTraces.filter(
                (currentIPTrace: IIPTrace) => currentIPTrace.ID !== ipTraceID,
              );
              this.setState({
                ipTraces,
                snackbarMessage: 'Successfully deleted IP Trace',
                snackbarError: false,
                updating: false,
              });
            })
            .catch((err) => {
              console.error(err);
              this.setState({
                snackbarMessage: 'Error while deleting IP Trace',
                snackbarError: true,
                updating: false,
              });
            });
        },
      );

      // the delete modal expects a promise to be returned
      return new Promise((resolve) => resolve(undefined));
    };

    // --------------- EVENT NOTIFICATION HANDLER ---------------
    handleCloseSnackbar = (): void => {
      this.setState({
        snackbarMessage: '',
        snackbarError: false,
      });
    };

    componentDidMount() {
      const { session } = this.props;
      setTimeout(() => {
        this.setState({
          loading: false,
        });
      }, 2000);

      this.setState(
        {
          loading: true,
        },
        () => {
          getIPTraces(session.publisher.ID)
            .then((ipTraces) => {
              this.setState({
                ipTraces,
                loading: false,
              });
            })
            .catch(() => {
              this.setState({
                snackbarMessage: 'failed to get IP Trace list',
                snackbarError: true,
                loading: false,
              });
            });
        },
      );
    }

    render() {
      const { classes, user, session, location } = this.props;
      const { pathname } = location;
      const { ipTraces, loading, snackbarError, snackbarMessage, updating } = this.state;

      // Transform IPTrace data to ListTable-compatible format
      let data: IIPTraceRowData[] = ipTraces.map((ipTrace: IIPTrace) => {
        return {
          ID: ipTrace.ID,
          Name: `IP Trace: ${ipTrace.IP}`,
          UUID: ipTrace.UUID,
          UserID: ipTrace.UserID,
          IP: ipTrace.IP,
          Email: ipTrace.Email,
          StartDate: moment(ipTrace.StartDate).format('YYYY-MM-DD HH:mm:ss'),
          TimeFrame: `${moment
            .duration(moment(ipTrace.EndDate).diff(moment(ipTrace.StartDate)))
            .asHours()} Hour`,
        };
      });

      // only keep the ip traces created by the logged in user (unless super user)
      if (!hasRoleSuperUser(user.user)) {
        data = data.filter((ipTrace) => ipTrace.UserID === user.user.ID);
      }

      if (ipTraces.length === 0 && loading) {
        return <LoadingView active={true} text='Loading IP Traces' />;
      }

      return (
        <Panels maxWidth vertical relative minWidthMain>
          <Panel
            largeHorizontalPadding
            verticalPadding
            style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}
          >
            <ModelTopBar
              title={routeName(pathname)}
              session={session}
              user={user}
              publisher={session.publisher}
              classes={classes}
              delegateElevate={true}
              currentPath={pathname}
              disableTitlePaddingLeft
            />
          </Panel>
          <Panel scroll largePadding>
            <Grid container justify='flex-end' style={{ paddingTop: 25, paddingBottom: 40 }}>
              <Grid
                item
                style={{
                  paddingRight: '20px',
                }}
              >
                <RowButton
                  right={
                    <ModelMediumButton
                      variant='contained'
                      color='primary'
                      style={{ paddingLeft: '30px', paddingRight: '30px' }}
                      component={Link}
                      to={`/app/pub/${user.publisher.ID}/iptrace/new`}
                    >
                      + Create New
                    </ModelMediumButton>
                  }
                />
              </Grid>
            </Grid>
            <Grid
              item
              style={{
                paddingLeft: '20px',
                paddingRight: '20px',
              }}
            >
              <ListTable
                title=''
                columns={columns}
                data={data}
                isLoading={loading}
                isUpdating={updating}
                handleDeleteItem={this.handleDeleteIPTrace}
                handleSetUpdating={this.handleSetUpdating}
              />
            </Grid>
            <SnackbarView
              open={!!snackbarMessage}
              error={!!snackbarError}
              onClose={this.handleCloseSnackbar}
            >
              {snackbarMessage}
            </SnackbarView>
          </Panel>
        </Panels>
      );
    }
  },
);
